import { useEffect, useRef, useState } from 'react';
import type { Incident } from '../../services/incidentsApi';
import { incidentsApi } from '../../services/incidentsApi';
import {
  groupedEvidenceReviewHasCloudProof,
  parseIncidentActivityMs,
  pickGroupedEvidenceRepresentativeIncidentId,
} from '../../domain/incidents/groupedEvidenceRepresentativePick';

function evidenceLookupKey(incident: Incident | null | undefined): string {
  if (!incident) return '';
  const id = String(incident.incident_id || '').trim();
  if (!id) return '';
  const snap = String((incident as any).snapshot_status || '').trim().toUpperCase();
  const clip = String((incident as any).clip_status || '').trim().toUpperCase();
  const hasSnap = groupedEvidenceReviewHasCloudProof(incident) ? 'proof' : `${snap}|${clip}`;
  return [
    id,
    hasSnap,
    String(incident.incident_type || '').trim(),
    String(incident.zone_id || '').trim(),
    String(incident.camera_id || '').trim(),
    String(incident.device_id || '').trim(),
    String((incident as any).pattern_key || '').trim(),
  ].join('::');
}

export function useGroupedVerifyEvidenceBetterProof(args: {
  incident: Incident | null | undefined;
  enabled?: boolean;
  fromDate?: string;
  toDate?: string;
}): { betterProofIncidentId: string | null; loading: boolean } {
  const { incident, enabled = true, fromDate, toDate } = args;
  const incidentId = String(incident?.incident_id || '').trim();
  const lookupKey = evidenceLookupKey(incident);
  const hasCloudProof = groupedEvidenceReviewHasCloudProof(incident);
  const incidentType = incident?.incident_type || undefined;
  const zoneId = incident?.zone_id || undefined;
  const deviceId = incident?.device_id || undefined;
  const cameraId = incident?.camera_id || undefined;
  const patternKey = String((incident as any)?.pattern_key || '').trim();
  const [betterProofIncidentId, setBetterProofIncidentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const lastCompletedKeyRef = useRef<string>('');

  useEffect(() => {
    if (!enabled || !incidentId || !lookupKey) {
      setBetterProofIncidentId(null);
      setLoading(false);
      return;
    }
    // Avoid re-flashing "Checking…" on every quiet incident refresh.
    if (lastCompletedKeyRef.current === lookupKey) {
      return;
    }

    let cancelled = false;
    // Silent when current row already has proof — still look for a newer complete case.
    if (!hasCloudProof) setLoading(true);
    void (async () => {
      try {
        const listed = await incidentsApi.listIncidents(
          undefined,
          undefined,
          50,
          0,
          fromDate,
          toDate,
          incidentType,
          zoneId,
          deviceId,
          cameraId,
        );
        if (cancelled) return;
        const rows = listed?.incidents || [];
        const picked = pickGroupedEvidenceRepresentativeIncidentId(rows, {
          fallbackId: incidentId,
          preferCloudProof: true,
          patternKey,
        });
        const pickedRow = rows.find((row) => String(row?.incident_id || '').trim() === picked);
        const pickedComplete = groupedEvidenceReviewHasCloudProof(pickedRow);
        const pickedIsNewer =
          parseIncidentActivityMs(pickedRow) > parseIncidentActivityMs(incident);
        if (
          picked
          && picked !== incidentId
          && pickedComplete
          && (!hasCloudProof || pickedIsNewer)
        ) {
          setBetterProofIncidentId(picked);
        } else {
          setBetterProofIncidentId(null);
        }
        lastCompletedKeyRef.current = lookupKey;
      } catch {
        if (!cancelled) setBetterProofIncidentId(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    cameraId,
    deviceId,
    enabled,
    fromDate,
    hasCloudProof,
    incident,
    incidentId,
    incidentType,
    lookupKey,
    patternKey,
    toDate,
    zoneId,
  ]);

  return { betterProofIncidentId, loading };
}
